<template>
  <div class="cliente-side-drawer-pane cliente-side-drawer-pane--agendamento space-y-4 p-0">
    <ClienteDetailSection
      :title="t('kanban.taskDetail.sections.schedule.title')"
      :subtitle="t('workspaceClienteModal.agendamento.listSubtitle')"
    >
      <template #icon>
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>
      </template>
      <template #actions>
        <button
          type="button"
          class="px-3 py-1.5 text-xs font-semibold rounded-[4px] bg-gradient-to-r from-purple-500 to-indigo-600 text-white shadow-sm"
          @click="showScheduleModal = true"
        >
          {{ t('workspaceClienteModal.agendamento.new') }}
        </button>
      </template>
      <div v-if="loading" class="text-sm text-gray-500 py-4">{{ t('common.loading') }}</div>
      <div v-else-if="!agendamentosAtivos.length && !agendamentosHistorico.length" class="cliente-empty-state cliente-empty-state--agendamento">
        <svg class="cliente-empty-state__icon" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>
        <p>{{ t('workspaceClienteModal.agendamento.empty') }}</p>
        <button type="button" class="cliente-empty-state__cta" @click="showScheduleModal = true">{{ t('workspaceClienteModal.agendamento.new') }}</button>
      </div>
      <template v-else>
        <div v-if="agendamentosAtivos.length" class="space-y-3">
          <h4 class="cliente-ag-section-title">{{ t('workspaceClienteModal.agendamento.sectionUpcoming') }}</h4>
          <ClienteAgendamentoCard
            v-for="ag in agendamentosAtivos"
            :key="ag.id"
            :agendamento="ag"
            :is-next="ag.id === nextAgendamentoId"
            :locale="dateLocale"
            :show-actions="true"
            :show-delete="canDeleteAgendamento"
            @status-change="updateAgendamentoStatus"
            @delete="requestDeleteAgendamento(ag)"
          />
        </div>
        <div v-if="agendamentosHistorico.length" class="space-y-3" :class="{ 'mt-6': agendamentosAtivos.length }">
          <h4 class="cliente-ag-section-title">{{ t('workspaceClienteModal.agendamento.sectionHistory') }}</h4>
          <ClienteAgendamentoCard
            v-for="ag in agendamentosHistorico"
            :key="'hist-' + ag.id"
            :agendamento="ag"
            :locale="dateLocale"
            :show-actions="false"
            :show-delete="canDeleteAgendamento"
            @delete="requestDeleteAgendamento(ag)"
          />
        </div>
      </template>
    </ClienteDetailSection>

    <ScheduleModal
      :is-open="showScheduleModal"
      :cliente="{ id: clienteId, workspaceId }"
      :motivos-agendamento="motivosAgendamento"
      @close="showScheduleModal = false"
      @scheduled="onScheduled"
    />

    <ClienteConfirmModal
      :open="!!confirmDeleteTarget"
      :title="t('workspaceClienteModal.agendamento.deleteConfirmTitle')"
      :message="deleteConfirmMessage"
      :loading="deleting"
      @cancel="cancelDeleteAgendamento"
      @confirm="confirmDeleteAgendamento"
    />
  </div>
</template>

<script setup>
import { ref, computed, watch, toRef } from 'vue'
import { useI18n } from 'vue-i18n'
import api from '@/services/api'
import ClienteDetailSection from '@/components/cliente/ClienteDetailSection.vue'
import ClienteAgendamentoCard from '@/components/cliente/ClienteAgendamentoCard.vue'
import ClienteConfirmModal from '@/components/cliente/ClienteConfirmModal.vue'
import ScheduleModal from '@/components/ScheduleModal.vue'
import { useAgendamentoStaffDelete } from '@/composables/useAgendamentoStaffDelete'
import '@/assets/styles/cliente-modal-theme.css'

const props = defineProps({
  clienteId: { type: Number, required: true },
  workspaceId: { type: Number, required: true },
  active: { type: Boolean, default: false },
  motivosAgendamento: { type: Array, default: () => [] }
})

const { t, locale } = useI18n()

const loading = ref(false)
const agendamentos = ref([])
const showScheduleModal = ref(false)

const workspaceIdRef = toRef(props, 'workspaceId')

const dateLocale = computed(() => {
  if (locale.value === 'en') return 'en-US'
  if (locale.value === 'es') return 'es'
  return 'pt-BR'
})

function parseAgendamentoDateTime (ag) {
  const raw = ag?.dataAgendamento
  if (!raw) return null
  const hora = String(ag.horario || '00:00').slice(0, 5)
  const d = new Date(String(raw).includes('T') ? raw : `${raw}T${hora}:00`)
  return Number.isNaN(d.getTime()) ? null : d
}

function normalizeAgendamentoStatus (ag) {
  return String(ag?.status || 'AGENDADO').trim().toUpperCase()
}

function isAgendamentoFuture (ag) {
  const d = parseAgendamentoDateTime(ag)
  return d != null && d >= new Date()
}

function compareAgendamentoAsc (a, b) {
  const da = parseAgendamentoDateTime(a)?.getTime() ?? 0
  const db = parseAgendamentoDateTime(b)?.getTime() ?? 0
  return da - db
}

function compareAgendamentoDesc (a, b) {
  return compareAgendamentoAsc(b, a)
}

const agendamentosAtivos = computed(() =>
  agendamentos.value
    .filter((ag) => normalizeAgendamentoStatus(ag) === 'AGENDADO' && isAgendamentoFuture(ag))
    .sort(compareAgendamentoAsc)
)

const agendamentosHistorico = computed(() => {
  const ativoIds = new Set(agendamentosAtivos.value.map((a) => a.id))
  return agendamentos.value
    .filter((ag) => !ativoIds.has(ag.id))
    .sort(compareAgendamentoDesc)
})

const nextAgendamentoId = computed(() => agendamentosAtivos.value[0]?.id ?? null)

async function loadAgendamentos () {
  if (!props.clienteId) return
  loading.value = true
  try {
    const { data } = await api.get(`/agendamentos/cliente/${props.clienteId}`)
    agendamentos.value = Array.isArray(data) ? data : []
  } catch {
    agendamentos.value = []
  } finally {
    loading.value = false
  }
}

const {
  canDeleteAgendamento,
  confirmDeleteTarget,
  deleting,
  loadStaffRole,
  requestDeleteAgendamento,
  cancelDeleteAgendamento,
  confirmDeleteAgendamento
} = useAgendamentoStaffDelete(workspaceIdRef, loadAgendamentos)

const deleteConfirmMessage = computed(() => {
  const ag = confirmDeleteTarget.value
  if (!ag) return t('workspaceClienteModal.agendamento.deleteConfirmMessage')
  const date = ag.dataAgendamento || ''
  const time = ag.horario || ''
  return t('workspaceClienteModal.agendamento.deleteConfirmMessageDetail', { date, time })
})

async function onScheduled () {
  showScheduleModal.value = false
  await loadAgendamentos()
}

async function updateAgendamentoStatus ({ id, status }) {
  try {
    await api.patch(`/agendamentos/cliente/agendamento/${id}/status`, { status })
    await loadAgendamentos()
  } catch (error) {
    console.error(error)
  }
}

watch(
  () => [props.clienteId, props.active, props.workspaceId],
  ([id, active, workspaceId]) => {
    if (workspaceId && active) loadStaffRole()
    if (id && active) loadAgendamentos()
  },
  { immediate: true }
)
</script>
